<template>
    <v-container fluid class="page-container">
        <page-header title="Teams" :subtitle="totalLabel">
            <v-btn v-if="isAdmin" color="primary" depressed @click="createItem">
                <v-icon left>mdi-plus</v-icon>New team
            </v-btn>
        </page-header>

        <filter-panel
            :fields="filterFields"
            :value="listState"
            :loading="loading"
            @apply="applyFilters"
        />

        <v-card outlined>
            <v-data-table
                :headers="visibleHeaders"
                v-bind="tableBindings"
                class="list-table clickable-rows"
                no-data-text="No teams match these filters"
                @update:options="onTableOptions"
                @click:row="openItem($event)"
            >
                <template v-slot:[`item.title`]="{ item }">
                    <span class="font-weight-medium">{{ item.title }}</span>
                </template>
                <template v-slot:[`item.available`]="{ item }">
                    <v-icon small :color="item.available ? 'success' : 'grey'">
                        {{ item.available ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                    </v-icon>
                </template>
                <template v-slot:[`item.dealer`]="{ item }">{{ dealerName(item.dealer) }}</template>
                <template v-slot:[`item.approved`]="{ item }">
                    <v-chip
                        small
                        :outlined="!item.approved"
                        :color="item.approved ? 'success' : 'error'"
                        :disabled="togglingId === item.id"
                        :title="isAdmin ? 'Click to toggle' : ''"
                        @click.stop="isAdmin && toggleApprove(item)"
                    >
                        {{ item.approved ? 'Approved' : 'Pending' }}
                    </v-chip>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex justify-end">
                        <v-btn icon small color="primary" title="Edit" @click.stop="openItem(item, 'edit')">
                            <v-icon small>mdi-pencil-outline</v-icon>
                        </v-btn>
                        <delete-button
                            v-if="isAdmin"
                            title="Delete this team?"
                            @delete-entry="deleteItem(item, deleteTeam, 'Team')"
                        />
                    </div>
                </template>
            </v-data-table>
            <table-footer v-model="page" :page-count="pageCount" :total="total" :items-per-page="itemsPerPage" />
        </v-card>
    </v-container>
</template>

<script>
    import teamService from '@/services/team-service'
    import dealerService from '@/services/dealer-service'
    import paginatedList from '@/mixins/paginated-list'
    import PageHeader from '@/components/PageHeader'
    import FilterPanel from '@/components/FilterPanel'
    import TableFooter from '@/components/TableFooter'
    import DeleteButton from '@/components/DeleteButton'
    import { fullName } from '@/utils/format'

    export default {
        name: 'TeamRoute',
        components: { PageHeader, FilterPanel, TableFooter, DeleteButton },
        mixins: [paginatedList],
        data() {
            return {
                dealers: null,
                togglingId: null,
                headers: [
                    { text: 'Id', value: 'id', width: 80 },
                    { text: 'Team', value: 'title' },
                    { text: 'Address', value: 'address' },
                    { text: 'Available', value: 'available', align: 'center' },
                    { text: 'Dealer', value: 'dealer' },
                    { text: 'Status', value: 'approved' },
                    { text: '', value: 'actions', align: 'end', sortable: false, width: 96 }
                ]
            }
        },
        computed: {
            filterFields() {
                const fields = [
                    { key: 'team', label: 'Team', placeholder: 'Title or id' },
                    { key: 'address', label: 'Address' },
                    { key: 'approved', label: 'Approved', type: 'boolean' }
                ]
                if (this.isAdmin) {
                    fields.push({ key: 'dealer', label: 'Dealer', type: 'autocomplete', items: this.dealers, itemText: fullName })
                }
                return fields
            }
        },
        created() {
            if (this.isAdmin) {
                dealerService.getAllDealers().then(resp => {
                    this.dealers = resp.data.data
                }).catch(() => {
                    this.dealers = []
                })
            }
        },
        methods: {
            fetchPage({ page, perPage, filters, sortOption, order }) {
                return teamService.getTeamsByFilter(page, filters, sortOption, order, perPage)
            },
            deleteTeam: id => teamService.deleteTeamById(id),
            dealerName(dealer) {
                return dealer && typeof dealer === 'object' ? fullName(dealer) : ''
            },
            toggleApprove(team) {
                this.togglingId = team.id
                teamService.toggleApproved(team.id).then(() => {
                    team.approved = !team.approved
                    this.$notify.success(`${team.title} is now ${team.approved ? 'approved' : 'pending'}`)
                }).catch(error => {
                    this.$notify.error(error, 'Could not change the approval')
                }).finally(() => {
                    this.togglingId = null
                })
            }
        }
    }
</script>
